/* oxlint-disable func-names */
import { AppBrandConfigs, targetBrandConfig } from 'AppBranding';
import ClientStorage from 'ClientStorage';
import { getRouteMetadata } from 'FrontRoyalRouteRegistry';
import { animatePathChange } from 'RouteAnimation';
import { storeProvider } from 'ReduxHelpers';
import { themeActions } from 'Theme/redux/theme';
import { navigationHelper } from 'NavigationHelpers';
// Import directly rather than from the module root. The StreamDashboard root pulls in the page,
// which reaches AppHeaderReact and leaves NavigationSectionKey undefined at module-init time.
import { popLearningBreadcrumbs } from 'StreamDashboard/popLearningBreadcrumbs';
// Import directly from sync module to avoid loading hooks which have NavigationSectionKey dependency
import { appHeaderSynchronizer } from 'AppHeaderReact/sync';
import angularModule from './navigation_module';
import Capabilities from 'Capabilities';

/**
 * Singleton view model for the app's header.
 * Inject into states to allow them to adjust things like the back button's URL and visibility
 */
angularModule.factory('Navigation.AppHeader.AppHeaderViewModel', [
    '$injector',
    $injector => {
        const AClassAbove = $injector.get('AClassAbove');
        const $window = $injector.get('$window');
        const reactRouterBridge = $injector.get('reactRouterBridge');
        const $timeout = $injector.get('$timeout');
        const safeApply = $injector.get('safeApply');
        const SiteMetadata = $injector.get('SiteMetadata');
        const $rootScope = $injector.get('$rootScope');
        const $sce = $injector.get('$sce');
        const TranslationHelper = $injector.get('TranslationHelper');
        const ConfigFactory = $injector.get('ConfigFactory');
        const scrollHelper = $injector.get('scrollHelper');
        const EventLogger = $injector.get('EventLogger');
        const { loadRoute, goBack } = navigationHelper();

        const AppHeaderViewModel = AClassAbove.subclass(function () {
            const translationHelper = new TranslationHelper('navigation.app_header_view_model');

            let BEGIN_LESSON_INSTRUCTIONS;
            let BEGIN_TEST_INSTRUCTIONS;
            let END_LESSON_INSTRUCTIONS;
            let END_TEST_INSTRUCTIONS;
            let SCREEN_COMPLETE_INSTRUCTIONS;

            this.refreshTranslations = () => {
                BEGIN_LESSON_INSTRUCTIONS = translationHelper.get('begin_lesson');
                BEGIN_TEST_INSTRUCTIONS = translationHelper.get('begin_test');
                END_LESSON_INSTRUCTIONS = translationHelper.get('lesson_complete');
                END_TEST_INSTRUCTIONS = translationHelper.get('test_complete');
                SCREEN_COMPLETE_INSTRUCTIONS = translationHelper.get('screen_complete');
            };
            // Note: we don't need to call this.refreshTranslations(); here because it will be called for us by
            // addPartAndRefreshTranslationTables() in translation_module.js, which in turn is called by
            // loadGlobalDependencies() via AssetLoader

            Object.defineProperty(this.prototype, 'layout', {
                get() {
                    const metadata = getRouteMetadata(reactRouterBridge.path()) ?? undefined;
                    const headerLayout = metadata?.headerLayout;
                    if (!headerLayout || headerLayout === 'default') {
                        return 'default';
                    }

                    return headerLayout;
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'frameInstructions', {
                get() {
                    if (!this.playerViewModel) {
                        return null;
                    }

                    if (this.playerViewModel.showStartScreen) {
                        return this.playerViewModel.lesson.test ? BEGIN_TEST_INSTRUCTIONS : BEGIN_LESSON_INSTRUCTIONS;
                    }

                    if (this.playerViewModel.showFinishScreen) {
                        return this.playerViewModel.lesson.test ? END_TEST_INSTRUCTIONS : END_LESSON_INSTRUCTIONS;
                    }

                    if (
                        this.playerViewModel.activeFrameViewModel &&
                        this.playerViewModel.activeFrameViewModel.complete
                    ) {
                        return SCREEN_COMPLETE_INSTRUCTIONS;
                    }

                    if (this.playerViewModel.activeFrameViewModel) {
                        return this.playerViewModel.activeFrameViewModel.frame.miniInstructions;
                    }

                    return null;
                },
            });

            let _currentFrameInstructionsForView = '';
            let _currentFrameInstructionsDelaying = false;

            // If frame instructions have changed since last time, we want the view to clear itself first,
            // then after a tiny delay show the new instructions. So, we track the last frame instruction value
            // and manually delay returning the new value.
            Object.defineProperty(this.prototype, 'frameInstructionsForView', {
                get() {
                    let valueToReturn = '';

                    // If a delay is in progress, just continue to return blank
                    if (_currentFrameInstructionsDelaying) {
                        valueToReturn = '';
                        // If the value has changed since last time, schedule a new delayed update
                    } else if (_currentFrameInstructionsForView !== this.frameInstructions) {
                        // After short delay, set the appropriate new value
                        let delay = 10;

                        // special case: if coming from an end screen, add more delay
                        // this gives time for the exit animations to complete from those screens

                        if (
                            [
                                BEGIN_LESSON_INSTRUCTIONS,
                                BEGIN_TEST_INSTRUCTIONS,
                                END_LESSON_INSTRUCTIONS,
                                END_TEST_INSTRUCTIONS,
                            ].includes(_currentFrameInstructionsForView)
                        ) {
                            delay = 1500;
                        }

                        // Make a note of the fact that we're delaying now
                        _currentFrameInstructionsDelaying = true;
                        // Trigger the delay
                        $timeout(() => {
                            // Update the current frame instructions for the view and trigger a scope apply
                            safeApply($rootScope, () => {
                                _currentFrameInstructionsForView = this.frameInstructions;
                                _currentFrameInstructionsDelaying = false;
                            });
                        }, delay);

                        valueToReturn = '';
                        // In the steady state, just return the current value
                    } else {
                        valueToReturn = _currentFrameInstructionsForView;
                    }

                    return valueToReturn;
                },
            });

            Object.defineProperty(this.prototype, 'bodyBackgroundColor', {
                get() {
                    const bodyClass = $('body').attr('class') || '';
                    return (bodyClass.match(/bg-(\w+)/) || [])[1];
                },
            });

            Object.defineProperty(this.prototype, 'hasTopMessage', {
                get() {
                    return this._hasTopMessage ?? false;
                },
                set(val) {
                    const newVal = !!val;
                    if (this._hasTopMessage !== newVal) {
                        this._hasTopMessage = newVal;
                        appHeaderSynchronizer.syncFromAngular('hasTopMessage', newVal);
                    }
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'showCaret', {
                get() {
                    return this._showCaret ?? false;
                },
                set(val) {
                    const newVal = !!val;
                    if (this._showCaret !== newVal) {
                        this._showCaret = newVal;
                        appHeaderSynchronizer.syncFromAngular('showCaret', newVal);
                    }
                },
            });

            Object.defineProperty(this.prototype, 'hideMobileMenu', {
                get() {
                    if (this.playerViewModel) return true;
                    if (this._mobileKeyboardShowing) return true;

                    // When a route is configured to not use the shell, the mobile menu
                    // will not be on the screen.
                    const metadata = getRouteMetadata(reactRouterBridge.path()) ?? undefined;
                    if (metadata?.removeAppShell === true) return true;
                    return false;
                },
            });

            // Synced property: showAlternateHomeButton
            Object.defineProperty(this.prototype, 'showAlternateHomeButton', {
                get() {
                    return this._showAlternateHomeButton ?? false;
                },
                set(val) {
                    const newVal = !!val;
                    if (this._showAlternateHomeButton !== newVal) {
                        this._showAlternateHomeButton = newVal;
                        appHeaderSynchronizer.syncFromAngular('showAlternateHomeButton', newVal);
                    }
                },
                configurable: true,
            });

            // Synced property: title
            Object.defineProperty(this.prototype, 'title', {
                get() {
                    return this._title;
                },
                set(val) {
                    if (this._title !== val) {
                        this._title = val;
                        appHeaderSynchronizer.syncFromAngular('title', val);
                    }
                },
                configurable: true,
            });

            // Synced property: titleOpts
            Object.defineProperty(this.prototype, 'titleOpts', {
                get() {
                    return this._titleOpts ?? {};
                },
                set(val) {
                    const newVal = val ?? {};
                    // Simple comparison for objects - could be improved
                    if (JSON.stringify(this._titleOpts) !== JSON.stringify(newVal)) {
                        this._titleOpts = newVal;
                        appHeaderSynchronizer.syncFromAngular('titleOpts', newVal);
                    }
                },
                configurable: true,
            });

            // Synced property: viewedStream
            Object.defineProperty(this.prototype, 'viewedStream', {
                get() {
                    return this._viewedStream ?? null;
                },
                set(val) {
                    if (this._viewedStream !== val) {
                        this._viewedStream = val;
                        appHeaderSynchronizer.syncFromAngular('viewedStream', val);
                    }
                },
                configurable: true,
            });

            // selectors used during background updates
            const bodyElem = $('body');
            const htmlElem = $('html');
            const metaThemeColorElem = $('meta[name=theme-color]');
            const msapplicationTileColor = $('meta[name=msapplication-TileColor]');

            return {
                initialize() {
                    // See See routing.md -> "Route Animations and Back Navigation" for some
                    // information about when showAlternateHomeButton is used as a back button.
                    this.showAlternateHomeButton = false;
                    this.showFrameInstructions = true;
                    this.preventFrameInstructions = false;
                    this.showMobileMessages = true;
                    this.textRows = undefined;
                    this.playerViewModel = undefined;
                    this.setTitleHTML();
                    this.allowTapScroll = Capabilities.touchEnabled;
                },

                toggleVisibility(visible) {
                    if (!visible) {
                        $('body').addClass('header-invisible');
                    } else {
                        $('body').removeClass('header-invisible');
                    }
                },

                goToMarketingPages() {
                    $window.location.href = '/';
                },

                goHome() {
                    // No need to reload if already home
                    const homePath = $rootScope.homePath;
                    if (reactRouterBridge.url() !== '/' && reactRouterBridge.url() !== homePath) {
                        EventLogger.log('debug_onboarding_issue:app_header_view_model_go_home', {
                            params: { homePath },
                        });
                        loadRoute($rootScope.homePath);
                        SiteMetadata.updateHeaderMetadata();
                    }
                },

                followLearningBreadcrumbsBack() {
                    const path = popLearningBreadcrumbs(reactRouterBridge.path());
                    animatePathChange(path, 'backward');
                },

                backgroundColors: {
                    // keys are background colors, values are
                    // associated text colors

                    // BACKGROUND_COLOR: TEXT_COLOR
                    blue: 'white',
                    purple: 'white',
                    coral: 'eggplant',
                    turquoise: 'white',

                    // 'eggplant': 'white',
                    // 'plum': 'white',

                    landing: 'white',

                    'completion-blue': 'white',

                    white: '',
                    beige: '',
                    'beige-pattern': '',
                    'demo-pattern': '',
                    'slate-grey-100-alt': '',
                },

                setBodyBackground(targetColor) {
                    // add the 'bg-TARGET_COLOR' class to sp-page
                    // and remove any other ones.  Also set the
                    // background on the header to the same color (
                    // I tried just making it transparent, but the
                    // transition looks better this way)

                    // attempt to prevent weird Safari bug during iframe unloads
                    if (!Object || !Object.keys) {
                        return;
                    }

                    const colors = Object.keys(this.backgroundColors);

                    targetColor = targetColor || 'slate-grey-100-alt';

                    // Since the store might not always be defined in specs, we add the
                    // elvis operator here.
                    storeProvider.store?.dispatch(themeActions.setBgColor({ bgColor: targetColor }));

                    if (!colors.includes(targetColor)) {
                        throw new Error(`unsupported background color "${targetColor}"`);
                    }
                    const isNotchDevice = htmlElem.hasClass('notch');

                    // clear out stale background, but don't remove the color if we're about to just re-add it (thrashing)
                    colors.forEach(color => {
                        if (color === targetColor) {
                            return;
                        }
                        bodyElem.removeClass(`bg-${color}`);
                        if (isNotchDevice) {
                            htmlElem.removeClass(`bg-${color}`);
                        }
                    });
                    bodyElem.addClass(`bg-${targetColor}`);

                    // The appShell directive is responsible for instantiating the AppHeaderViewModel singleton
                    // and it only does so AFTER calling ConfigFactory.getConfig(), so it should be fine to call
                    // ConfigFactory.getSync() here.
                    const config = ConfigFactory.getSync();

                    // This is putting the brand class on the body element. As we transition to react, we eventually
                    // won't need this anymore. Instead, we're setting the brand class in ReactWrapper.
                    let _targetBrandConfig = targetBrandConfig($rootScope.currentUser, config);
                    const serverBrand = ClientStorage.getItem('serverBrand');
                    if (serverBrand && serverBrand in AppBrandConfigs && !$rootScope.currentUser) {
                        _targetBrandConfig = AppBrandConfigs[serverBrand];
                    }
                    Object.values(AppBrandConfigs).forEach(brandConfig => {
                        if (brandConfig.brandStyleClass !== _targetBrandConfig.brandStyleClass) {
                            bodyElem.removeClass(brandConfig.brandStyleClass);
                        }
                    });
                    bodyElem.addClass(_targetBrandConfig.brandStyleClass);
                    metaThemeColorElem.attr('content', _targetBrandConfig.themeColor);
                    msapplicationTileColor.attr('content', _targetBrandConfig.themeColor);

                    // Color the `html` element inset notches in some devices (unibrow header and home-drag indicator footer).
                    // Note that these html-element background colors are limited to content views, where the Coral coloring
                    // clashes. Some other non-content colors (beige, etc) default to the Coral wrapping color, because
                    // this matches the mobile menu button colors, whereas a continuation of the body background color would
                    // clash. Additionally, beyond visually clashing, patterned image backgrounds would be hard to align between
                    // html and body elements.
                    if (isNotchDevice && ['blue', 'purple', 'turquoise', 'completion-blue'].includes(targetColor)) {
                        htmlElem.addClass(`bg-${targetColor}`);
                    } else {
                        Object.values(AppBrandConfigs).forEach(brandConfig => {
                            if (
                                brandConfig.defaultNotchBackgroundClass !==
                                _targetBrandConfig.defaultNotchBackgroundClass
                            ) {
                                htmlElem.removeClass(brandConfig.defaultNotchBackgroundClass);
                            }
                        });
                        htmlElem.addClass(_targetBrandConfig.defaultNotchBackgroundClass);
                    }
                },

                scrollToTop(smooth) {
                    scrollHelper.scrollToTop(smooth);
                },

                backButtonClick() {
                    if (this.playerViewModel) {
                        // When there is a lesson, back-button-click will have a label.  In practice
                        // mode it will not.  Probably it should have the label 'practice' or something,
                        // but doesn't seem worth custom implementation
                        this.playerViewModel.log('lesson:back-button-click');
                        this.playerViewModel.gotoPrev();
                        document.activeElement.blur();
                    } else {
                        throw new Error('backButtonClick cannot be called without a playerViewModel.');
                    }
                },

                exitButtonClick() {
                    // We should have a PlayerViewModel most of the time
                    if (this.playerViewModel) {
                        if (this.playerViewModel.previewMode) {
                            return;
                        }

                        if (this.playerViewModel.demoMode) {
                            $window.parent.postMessage('closeDemo', '*');
                        }
                        // And will usually exit to its dashboard
                        else if (this.playerViewModel.stream) {
                            this.setBodyBackground();
                            // If the user presses `back` after landing on the stream dashboard, we don't want them to go back to the player.
                            loadRoute(this.playerViewModel.stream.streamDashboardPath, { replace: true });
                            SiteMetadata.updateHeaderMetadata();
                        } else {
                            goBack();
                        }
                    }
                    // Unless we are transitioning between lessons
                    // In which case we cached the stream from the last PlayerViewModel
                    else if (this.lastPlayedStream) {
                        // So exit to it
                        // If the user presses `back` after landing on the stream dashboard, we don't want them to go back to the player.
                        loadRoute(this.lastPlayedStream.streamDashboardPath, { replace: true });
                        SiteMetadata.updateHeaderMetadata();
                    } else {
                        // It's inconclusive what state the user would have to be in for them to hit this point,
                        // but in the event that they do, just send them back from whence they came.
                        goBack();
                    }
                },

                demoLogoClick() {
                    // we're loaded in an iframe, so we ask the parent window to navigate up the page
                    $window.parent.postMessage('scrollUpPage', '*');
                },

                setTitleHTML(titleHTML, opts = {}) {
                    if (angular.isDefined(titleHTML)) {
                        $sce.trustAsHtml(titleHTML);
                        this.title = titleHTML;
                    } else if ($rootScope.currentUser && $rootScope.currentUser.name) {
                        this.title = $rootScope.currentUser.name.toUpperCase();
                    } else {
                        this.title = undefined;
                    }

                    this.titleOpts = opts;
                },

                refreshTranslations() {
                    AppHeaderViewModel.refreshTranslations();
                },
            };
        });

        // Singleton
        const viewModel = new AppHeaderViewModel();

        // Register with synchronizer for Angular-Redux state sync
        appHeaderSynchronizer.registerViewModel(viewModel);

        return viewModel;
    },
]);
